import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCheck, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { useLocationTracking } from '../../hooks'
import type { RecoveryObject } from '../../api'
import { LocationClock, type ClockSettings, type ClockFace } from './LocationClock'
import { LocationColumn } from './LocationColumn'
import { GlobalMap, type MapLocation } from '@/components/maps/GlobalMap'
import { applyRefresh, objKey, orderColumn, stillChanged, type Update } from './location-updates'

const CLOCK_SETTINGS_KEY = 'locationTracking.clockSettings'
const FACE_OPTIONS: { value: ClockFace; labelKey: string; fallback: string }[] = [
    { value: 'theme', labelKey: 'recoverRequests.locationTracking.faceTheme', fallback: 'Theme' },
    { value: 'refined-minimalist', labelKey: 'recoverRequests.locationTracking.faceRefinedMinimalist', fallback: 'Refined Minimalist' },
    { value: 'premium-blue', labelKey: 'recoverRequests.locationTracking.facePremiumBlue', fallback: 'Premium Blue' },
    { value: 'frosted-glass', labelKey: 'recoverRequests.locationTracking.faceFrostedGlass', fallback: 'Frosted Glass' },
    { value: 'dark-elegant', labelKey: 'recoverRequests.locationTracking.faceDarkElegant', fallback: 'Dark Elegant' },
]

/** A new update still flashes as it arrives — now at the top of its column, where it can be seen. */
const FLASH_TOTAL_MS = 1500

const INTERVAL_OPTIONS = [
    { value: '0', ms: 0, labelKey: 'recoverRequests.locationTracking.refreshOff', fallback: 'Off' },
    { value: '5', ms: 5000, labelKey: 'recoverRequests.locationTracking.refresh5s', fallback: '5s' },
    { value: '10', ms: 10000, labelKey: 'recoverRequests.locationTracking.refresh10s', fallback: '10s' },
    { value: '30', ms: 30000, labelKey: 'recoverRequests.locationTracking.refresh30s', fallback: '30s' },
    { value: '60', ms: 60000, labelKey: 'recoverRequests.locationTracking.refresh1m', fallback: '1m' },
]

export function LocationTrackingTab({ setId }: { setId: number }) {
    /** Show the map above the clocks. Off by default — the columns are the point of this tab. */
    const [showMap, setShowMap] = useState(false)
    const { t } = useTranslation()
    const [intervalValue, setIntervalValue] = useState('0')
    const refreshMs = INTERVAL_OPTIONS.find((o) => o.value === intervalValue)?.ms ?? 0

    // Clock display settings (persisted across sessions).
    const [clock, setClock] = useState<ClockSettings>(() => {
        try {
            const saved = localStorage.getItem(CLOCK_SETTINGS_KEY)
            if (saved) {
                const merged = { face: 'theme', showSeconds: true, showAnalog: true, ...JSON.parse(saved) } as ClockSettings
                // Drop removed/unknown faces (e.g. legacy 'vault-sentinel') back to the drawn theme face.
                if (!FACE_OPTIONS.some((o) => o.value === merged.face)) merged.face = 'theme'
                return merged
            }
        } catch { /* ignore */ }
        return { face: 'theme', showSeconds: true, showAnalog: true }
    })
    useEffect(() => {
        try { localStorage.setItem(CLOCK_SETTINGS_KEY, JSON.stringify(clock)) } catch { /* ignore */ }
    }, [clock])

    const { data, isLoading, isError, refetch, isFetching } = useLocationTracking(setId, { refreshMs })

    // ── Unseen updates (lib: location-updates) ─────────────────────────────
    // Kept until SEEN, not until a timer runs out: the old 6.5-second highlight was usually over
    // before anyone scrolled to it.
    const [updates, setUpdatesState] = useState<Map<string, Update>>(new Map())
    // Mirrored in a ref so a refresh can compute the new set synchronously and know, there and
    // then, which updates have just arrived (a state updater may run later, after the flash
    // decision has already been made on stale data).
    const updatesRef = useRef<Map<string, Update>>(updates)
    const setUpdates = (next: Map<string, Update>) => { updatesRef.current = next; setUpdatesState(next) }
    const [flashing, setFlashing] = useState<Set<string>>(new Set())
    const prevRef = useRef<RecoveryObject[] | null>(null)
    const timers = useRef<number[]>([])
    useEffect(() => () => { timers.current.forEach((id) => clearTimeout(id)) }, [])

    useEffect(() => {
        if (!data) return
        const objects = data.objects || []
        const prev = prevRef.current
        prevRef.current = objects
        const now = Date.now()
        const current = new Map(objects.map((o) => [objKey(o), o]))
        const next = applyRefresh(updatesRef.current, prev, objects, now)
        for (const [key, u] of next) {
            const o = current.get(key)
            // Gone from the board, or back where it was when last seen: nothing to show.
            if (!o || !stillChanged(u, o)) next.delete(key)
        }
        setUpdates(next)
        const arrived = [...next].filter(([, u]) => u.at === now).map(([key]) => key)
        if (arrived.length === 0) return
        setFlashing((f) => new Set([...f, ...arrived]))
        const id = window.setTimeout(() => {
            setFlashing((f) => { const n = new Set(f); arrived.forEach((k) => n.delete(k)); return n })
        }, FLASH_TOTAL_MS)
        timers.current.push(id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    const markSeen = (keys: string[]) => {
        const m = updatesRef.current
        if (!keys.some((k) => m.has(k))) return
        const n = new Map(m); keys.forEach((k) => n.delete(k)); setUpdates(n)
    }
    const unseenTotal = updates.size

    /** Same mapping the Map tab uses, so both read the one payload. */
    const mapLocations: MapLocation[] = useMemo(
        () => (data?.locations ?? []).map((l: any) => ({
            id: String(l.id),
            name: l.description || l.name || 'Unknown',
            coords: (l.latitude != null && l.longitude != null)
                ? [Number(l.longitude), Number(l.latitude)] as [number, number]
                : null,
            category: l.category || '',
            place: l.name || '',
            objects: Number(l.objectCount || 0),
            engineers: Number(l.assignedEngineerCount || 0),
            earliestStart: l.earliestStart ?? null,
            highestRta: l.highestRta ?? null,
            // The same field the clocks below this map are driven from.
            timeZone: l.timeZone ?? null,
        })),
        [data]
    )

    // ── Group objects under their location ─────────────────────────────────
    const locations = data?.locations ?? []
    const objectsByLocation = useMemo(() => {
        const map = new Map<number, RecoveryObject[]>()
        for (const o of data?.objects ?? []) {
            const arr = map.get(o.locationId) || []
            arr.push(o)
            map.set(o.locationId, arr)
        }
        return map
    }, [data])


    return (
        <div className="space-y-4">
            {/* Controls */}
            <div className="flex items-center justify-between gap-2">
                <div>
                    <h2 className="text-lg font-semibold text-foreground">
                        {t('recoverRequests.locationTracking.title', 'Location Tracking')}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        {t('recoverRequests.locationTracking.description', 'Local time and live engineer/runbook progress per recovery location.')}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {unseenTotal > 0 && (
                        <Button variant="outline" size="sm" onClick={() => setUpdates(new Map())} data-testid="mark-all-seen">
                            <CheckCheck className="h-4 w-4" />
                            <span className="ml-1.5">
                                {t('recoverRequests.locationTracking.markAllSeen', 'Mark all seen ({{count}})', { count: unseenTotal })}
                            </span>
                        </Button>
                    )}
                    {/* The map is its own tab, but it is also useful beside the clocks — so
                        this SHOWS it above them rather than replacing them. Off by default:
                        the columns are what this screen is for. */}
                    <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                        <input
                            type="checkbox"
                            checked={showMap}
                            onChange={(e) => setShowMap(e.target.checked)}
                            className="h-3.5 w-3.5 cursor-pointer accent-primary"
                        />
                        {t('recoverRequests.locationTracking.showMap', 'Show map')}
                    </label>
                    <Select value={intervalValue} onValueChange={setIntervalValue}>
                        <SelectTrigger className="w-28">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {INTERVAL_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                    {t(o.labelKey, o.fallback)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
                        <RefreshCw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                        <span className="ml-1.5">{t('common.refresh', 'Refresh')}</span>
                    </Button>
                </div>
            </div>

            {/* Clock display options */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border bg-muted/20 px-3 py-2">
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground">{t('recoverRequests.locationTracking.clockFace', 'Clock face')}</Label>
                    <Select value={clock.face} onValueChange={(v) => setClock((c) => ({ ...c, face: v as ClockFace }))}>
                        <SelectTrigger className="h-8 w-40">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {FACE_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                    {t(o.labelKey, o.fallback)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center gap-2">
                    <Switch id="lt-analog" checked={clock.showAnalog} onCheckedChange={(v) => setClock((c) => ({ ...c, showAnalog: v }))} />
                    <Label htmlFor="lt-analog" className="text-xs text-muted-foreground cursor-pointer">
                        {t('recoverRequests.locationTracking.analogClock', 'Analog clock')}
                    </Label>
                </div>
                <div className="flex items-center gap-2">
                    <Switch id="lt-seconds" checked={clock.showSeconds} onCheckedChange={(v) => setClock((c) => ({ ...c, showSeconds: v }))} />
                    <Label htmlFor="lt-seconds" className="text-xs text-muted-foreground cursor-pointer">
                        {t('recoverRequests.locationTracking.showSeconds', 'Show seconds')}
                    </Label>
                </div>
            </div>

            {isLoading ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                    {t('common.loading', 'Loading…')}
                </div>
            ) : isError ? (
                <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-8 text-center text-sm text-destructive">
                    {t('recoverRequests.locationTracking.error', 'Could not load location tracking data.')}
                </div>
            ) : locations.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                    {t('recoverRequests.locationTracking.empty', 'No recovery locations for this set.')}
                </div>
            ) : (
                <>
                    {showMap && (
                        <div className="rounded-lg border bg-card p-3">
                            <GlobalMap locations={mapLocations} height={420} />
                        </div>
                    )}

                    {/* Sticky clocks row */}
                    <div className="sticky top-0 z-10 -mx-1 rounded-lg border bg-background/95 px-1 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                        <div className="flex gap-4 overflow-x-auto pb-1">
                            {locations.map((loc) => (
                                <LocationClock key={loc.id} location={loc} settings={clock} />
                            ))}
                        </div>
                    </div>

                    {/* Engineer-update columns */}
                    <div className="flex gap-4 overflow-x-auto pb-2">
                        {locations.map((loc) => (
                            <LocationColumn
                                key={loc.id}
                                location={loc}
                                objects={orderColumn(objectsByLocation.get(loc.id) ?? [], updates)}
                                updates={updates}
                                flashing={flashing}
                                onSeen={markSeen}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}
